import React, { useEffect, useRef, useState } from "react";
import { Button, Typography } from "antd";
import {
  CameraOutlined,
  CloseOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { isValidCccdQr } from "helper/commonValidator";

const SNAP_SOUND = "./assets/audios/camera.mp3";
const BEEP_SOUND = "./assets/audios/beep.mp3";

/**
 * Props:
 * - onCapture(imageDataUrl)
 * - onQrDetected(qrText, imageDataUrl)
 * - isCameraOpen
 * - onCloseCamera()
 */

const isMobile = () => {
  return window.innerWidth <= 480
    ? "xs"
    : window.innerWidth <= 768
    ? "sm"
    : "md";
};

export default function QrCameraScanner({
  isCameraOpen,
  onCapture,
  onQrDetected,
  onCloseCamera,
}) {
  const [autoCaptured, setAutoCaptured] = useState(false);
  const regionId = "qr-reader-region-live";
  const mounted = useRef(false);

  // Hàm phát âm
  function playSound(type) {
    let audio = null;
    if (type === "snap") {
      audio = new window.Audio(SNAP_SOUND);
    } else if (type === "beep") {
      audio = new window.Audio(BEEP_SOUND);
    }
    if (audio) audio.play();
  }

  // Mount/Unmount & camera logic
  useEffect(() => {
    mounted.current = true;
    if (isCameraOpen) {
      (async () => {
        // Cleanup html5-qrcode instance if any
        if (window.html5QrObj) {
          try {
            await window.html5QrObj.stop();
          } catch {}
          try {
            await window.html5QrObj.clear();
          } catch {}
          window.html5QrObj = null;
        }
        // Cleanup region
        const region = document.getElementById(regionId);
        if (region) region.innerHTML = "";
        try {
          const { Html5Qrcode } = await import("html5-qrcode");
          const config = {
            fps: 15,
            qrbox: {
              width: isMobile() === "xs" ? 300 : 395,
              height: isMobile() === "xs" ? 190 : 250,
            },
            aspectRatio: 1.58,
            rememberLastUsedCamera: true,
          };
          const html5Qr = new Html5Qrcode(regionId);
          window.html5QrObj = html5Qr;
          await html5Qr.start(
            { facingMode: "environment" },
            config,
            (decodedText) => {
              if (!isValidCccdQr(decodedText)) return;
              if (!autoCaptured && mounted.current) {
                setAutoCaptured(true);
                playSound("beep");
                captureFrame(decodedText, true);
              }
            }
          );
        } catch {}
      })();
    }
    // Cleanup on unmount or camera off
    return () => {
      mounted.current = false;
      (async () => {
        if (window.html5QrObj) {
          try {
            await window.html5QrObj.stop();
          } catch {}
          try {
            await window.html5QrObj.clear();
          } catch {}
          window.html5QrObj = null;
        }
      })();
    };
    // eslint-disable-next-line
  }, [isCameraOpen]);

  // Chụp ảnh thủ công hoặc tự động
  async function captureFrame(qrText = null, isAuto = false) {
    const video = document.querySelector(`#${regionId} video`);
    if (!video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    const targetRatio = 1.58;
    let cropW = w * 0.7;
    let cropH = cropW / targetRatio;
    if (cropH > h * 0.7) {
      cropH = h * 0.7;
      cropW = cropH * targetRatio;
    }
    const sx = (w - cropW) / 2;
    const sy = (h - cropH) / 2;
    const canvas = document.createElement("canvas");
    canvas.width = cropW;
    canvas.height = cropH;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, sx, sy, cropW, cropH, 0, 0, cropW, cropH);
    const imgUrl = canvas.toDataURL("image/png");

    if (!isAuto) playSound("snap");

    // Cleanup camera
    if (window.html5QrObj) {
      try {
        await window.html5QrObj.stop();
      } catch {}
      try {
        await window.html5QrObj.clear();
      } catch {}
      window.html5QrObj = null;
    }
    setAutoCaptured(false);
    if (onCloseCamera) onCloseCamera();

    if (isAuto && qrText) {
      onQrDetected && onQrDetected(qrText, imgUrl);
    } else {
      onCapture && onCapture(imgUrl);
    }
  }

  // UI
  if (!isCameraOpen) return null;

  return (
    <>
      <div className="qr-cccd-scanner__camera">
        {/* Camera */}
        <div className="qr-cccd-scanner__camera__region" id={regionId} />
        {/* Focus frame */}
        <div className="qr-cccd-scanner__camera__focus-frame">
          <div className="qr-cccd-scanner__camera__focus-frame__text">
            Đưa CCCD mặt trước đến trước camera để quét thông tin
          </div>
        </div>
      </div>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          gap: 12,
        }}
      >
        <Button
          type="primary"
          icon={<CameraOutlined />}
          size="large"
          onClick={() => captureFrame()}
        >
          Chụp ảnh
        </Button>
        <Button
          type="default"
          icon={<CloseOutlined />}
          size="large"
          danger
          onClick={() => {
            setAutoCaptured(false);
            if (onCloseCamera) onCloseCamera();
          }}
        >
          Tắt camera
        </Button>
      </div>
    </>
  );
}
